// @flow

import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { propEq } from "ramda";
import { Controller as FormFieldController, useForm } from "react-hook-form";

import { OkButton, TextField } from "@quantum/ui";
import { Translate, useI18n } from "@quantum/i18n";

import {
  abrirPaginaArquivos,
  excluirArquivo,
  salvarArquivo,
  salvandoArquivo,
  getNomeArquivoEmCache,
  isModoSeries,
} from "../../../state";
import {
  TIPO_ARQUIVO_CONFIGURACAO_RELATORIO,
  TIPO_ARQUIVO_SERIES,
} from "../../../model";

import Pagina from "../pagina";
import { useArquivosMedidas } from "../../remote";
import ListaArquivosMedidas from "../lista-arquivos-medidas";

import "./salvar-arquivo.css";

const SalvarArquivo = () => {
  const { translate } = useI18n();
  const dispatch = useDispatch();

  const salvando = useSelector(salvandoArquivo);
  const modoSeries = useSelector(isModoSeries);

  const nomeArquivoAberto = useSelector(getNomeArquivoEmCache);

  const { control, handleSubmit, setValue } = useForm({
    mode: "onBlur",
  });

  const defineNome = useCallback(nome => setValue("nome", nome), [setValue]);

  const aoSelecionar = useCallback(arquivo => defineNome(arquivo.nome), [
    defineNome,
  ]);
  const aoExcluir = useCallback(arquivo => dispatch(excluirArquivo(arquivo)), [
    dispatch,
  ]);

  const aoCompartilhar = useCallback(
    arquivo => dispatch(abrirPaginaArquivos("COMPARTILHAR", { arquivo })),
    [dispatch]
  );

  const {
    data: arquivos = [],
    loading: carregandoArquivos,
  } = useArquivosMedidas({ fetch: false });

  const aoSubmeter = useCallback(
    ({ nome }) => {
      const nomeAjustado = nome.trim();
      const arquivoExistente = arquivos.find(propEq("nome", nomeAjustado));
      if (arquivoExistente) {
        if (
          window.confirm(
            translate("configuracaoRelatorio.hintSobrescreverArquivo")
          )
        ) {
          dispatch(salvarArquivo(arquivoExistente));
        }
      } else {
        dispatch(
          salvarArquivo({
            id: null,
            nome: nomeAjustado,
            tipoArquivo: modoSeries
              ? TIPO_ARQUIVO_SERIES
              : TIPO_ARQUIVO_CONFIGURACAO_RELATORIO,
            dataModificacao: null,
          })
        );
      }
    },
    [dispatch, arquivos, translate, modoSeries]
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (nomeArquivoAberto != null) {
      setValue("nome", nomeArquivoAberto);
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <Pagina titulo={translate("configuracaoRelatorio.hintSalvarArquivo")}>
      <form className="SalvarArquivo__form" onSubmit={handleSubmit(aoSubmeter)}>
        <div className="SalvarArquivo__grupo">
          <label className="SalvarArquivo__label">
            <Translate>maiusculas.nomeArquivo</Translate>
          </label>
          <FormFieldController
            control={control}
            rules={{ required: true, minLength: 1 }}
            name="nome"
            defaultValue=""
            className="SalvarArquivo__texto"
            placeholder={translate(
              "configuracaoRelatorio.placeholderNomeArquivo"
            )}
            as={<TextField />}
          />
        </div>

        <div className="SalvarArquivo__grupo">
          <OkButton
            className="SalvarArquivo__ok"
            type="submit"
            disabled={salvando || carregandoArquivos}
          >
            {translate(
              salvando
                ? "configuracaoRelatorio.botaoSalvando"
                : "configuracaoRelatorio.botaoSalvar"
            )}
          </OkButton>
        </div>
      </form>

      <ListaArquivosMedidas
        handleExcluirArquivo={aoExcluir}
        handleSelecionarArquivo={aoSelecionar}
        handleCompartilharArquivo={aoCompartilhar}
      />
    </Pagina>
  );
};

export default SalvarArquivo;
